import { useRef, useMemo, useCallback, useState, useEffect } from 'react'
import {
  useGetInspectorDataLazyQuery,
  ArtboardDetailInfoFragment,
} from '@sketch/gql-types'
import positionLayersOnFauxArtboard from './positionLayersOnFauxArtboard'
import { useAnalytics } from '@sketch/modules-common'

export type Artboard = Pick<
  ArtboardDetailInfoFragment,
  'isPage' | 'page' | 'identifier'
>

const queryVariables = (artboard: Artboard) => {
  if (artboard.isPage && artboard.page && artboard.page.identifier) {
    return { pageId: artboard.page.identifier }
  } else {
    return { artboardId: artboard.identifier }
  }
}

export interface UseGetInspectorDataHookProps {
  artboard?: Artboard
  pollInterval?: number
  timeout?: number
}

const DEFAULT_TIMEOUT = 5 * 60 * 1000 // 5 minutes

const getTimeout = () => {
  try {
    const timeout = localStorage.getItem('debug.inspector.timeout')

    if (timeout) {
      console.warn(
        `Using ${timeout}ms from local storage. Remove custom timeout using: localStorage.removeItem('debug.inspector.timeout')`
      )
      return parseInt(timeout, 10)
    }
  } catch {
    return DEFAULT_TIMEOUT
  }

  return DEFAULT_TIMEOUT
}

const useGetInspectorData = ({
  artboard,
  pollInterval = 15000,
  timeout = getTimeout(),
}: UseGetInspectorDataHookProps) => {
  const [isPolling, setPolling] = useState(false)
  const [pollingFailed, setPollingFailed] = useState(false)
  const [
    inspectorLoadingStartTime,
    setInspectorLoadingStartTime,
  ] = useState<number>(0)
  const [inspectorLoaded, setInspectorLoaded] = useState(false)
  const [inspectorIsLoading, setInspectorIsLoading] = useState(false)
  const inspectorError = useRef<string>()
  const inspectorErrorReason = useRef<string>()
  const { trackMetric } = useAnalytics()
  const [
    loadInspectorData,
    {
      loading,
      data,
      startPolling: apolloStartPolling,
      stopPolling: apolloStopPolling,
      error,
    },
  ] = useGetInspectorDataLazyQuery()

  // if the inspector is set to loading, but the actual loading has finished and there's no data or error yet
  // this is the case when you're switching between artboards and want to record the time on each switch
  const inspectorPreviousLoad =
    inspectorIsLoading && !loading && !data && !error
  // or it's the first loading
  const inspectorFirstLoad = !inspectorIsLoading && loading

  if (inspectorPreviousLoad || inspectorFirstLoad) {
    setInspectorLoadingStartTime(Date.now())
    setInspectorIsLoading(true)
    setInspectorLoaded(false)
  }

  const load = useCallback(() => {
    if (artboard) {
      loadInspectorData({ variables: queryVariables(artboard) })
    }
  }, [loadInspectorData, artboard])

  const startPolling = () => {
    if (isPolling || !apolloStartPolling) {
      return
    }

    apolloStartPolling(pollInterval)
    setPolling(true)
  }

  const stopPolling = useCallback(() => {
    if (!isPolling || !apolloStopPolling) {
      return
    }

    apolloStopPolling()
    setPolling(false)
  }, [isPolling, apolloStopPolling])

  useEffect(() => {
    if (!isPolling) {
      return
    }
    const timeoutID = window.setTimeout(() => {
      inspectorError.current = 'NETWORK TIMEOUT ERROR'
      setPollingFailed(true)
      stopPolling()
    }, timeout)

    return () => {
      window.clearTimeout(timeoutID)
    }
  }, [isPolling, timeout, stopPolling])

  if (error) {
    stopPolling()
    const inspectorErrorExtensions = error?.graphQLErrors?.[0]?.extensions

    const errorCode =
      inspectorErrorExtensions !== undefined
        ? inspectorErrorExtensions['code']
        : 'ERROR'

    inspectorError.current = errorCode
    inspectorErrorReason.current =
      inspectorErrorExtensions !== undefined
        ? inspectorErrorExtensions['reason']
        : undefined
  }

  const inspector = data && data.inspectorData && data.inspectorData.inspector

  const inspectorData = useMemo(() => {
    if (loading) {
      return undefined
    }

    if (!inspector || !inspector.data || !artboard) {
      return undefined
    }

    let inspectorData
    try {
      inspectorData = JSON.parse(inspector.data)
    } catch (exception) {
      stopPolling()
      inspectorError.current =
        `Error parsing inspector data json string: "${inspector.data}", ` +
        `exception: ${exception}`
    }

    if (artboard.isPage && inspectorData) {
      inspectorData.layers = positionLayersOnFauxArtboard(inspectorData.layers)
    }

    return inspectorData
  }, [artboard, loading, inspector, inspectorError, stopPolling])

  // when we haven't set the inspectorLoaded yet, but the status is ready and we have the data
  const inspectorIsReady =
    !inspectorLoaded && inspector?.status === 'READY' && inspectorData

  // do not trigger this section forever if the start time is already zero (aka everything has loaded)
  if ((inspectorIsReady || error) && inspectorLoadingStartTime !== 0) {
    const finishedLoadTime = Date.now() - inspectorLoadingStartTime

    trackMetric({
      id: 'webapp_inspector_e2e_load_duration_milliseconds',
      value: finishedLoadTime,
      labels: { type: 'artboard' },
    })

    setInspectorLoadingStartTime(0)
    setInspectorLoaded(true)
    setInspectorIsLoading(false)
  }

  if (!loading && !pollingFailed && inspector) {
    if (inspector.status === 'PENDING') {
      startPolling()
    } else if (inspector.data) {
      stopPolling()
    }
  }

  return [
    load,
    {
      loading,
      inspectorData,
      inspectorError: inspectorError.current,
      inspectorErrorReason: inspectorErrorReason.current,
      isPolling,
    },
  ] as const
}

export default useGetInspectorData
